import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { History } from "lucide-react";
import { api, BRL } from "@/lib/api";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
};

export default function ItemHistoryDialog({ open, onOpenChange, budgetId, itemId, apiPrefix = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !budgetId || !itemId) return;
    setLoading(true);
    api.get(`${apiPrefix}/budgets/${budgetId}/items/${itemId}/history`)
      .then((r) => setData(r.data))
      .catch(() => setData({ current: null, versions: [] }))
      .finally(() => setLoading(false));
  }, [open, budgetId, itemId, apiPrefix]);

  const toggleVisible = async (v) => {
    const next = !(v.visible_to_school ?? true);
    try {
      await api.patch(`/budgets/${budgetId}/items/${itemId}/versions/${v.idx}`, { visible_to_school: next });
      setData((d) => ({ ...d, versions: d.versions.map((x) => (x.idx === v.idx ? { ...x, visible_to_school: next } : x)) }));
      toast.success(next ? "Diretor passa a ver esta alteração" : "Alteração escondida do diretor");
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="item-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1e3a8a]">
            <History className="w-5 h-5 text-[#7c3aed]" />
            Histórico do item
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-slate-500 py-6 text-center">Carregando...</p>
        ) : !data ? (
          <p className="text-sm text-slate-500 py-6 text-center">Sem histórico.</p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {data.current && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">Versão atual</p>
                <p className="text-sm font-semibold text-slate-900">{data.current.description}</p>
                {data.current.observation && (
                  <p className="text-xs text-slate-600 mt-0.5">{data.current.observation}</p>
                )}
                <p className="text-xs text-slate-700 mt-1">
                  Qtd {data.current.quantity} × {BRL(data.current.unit_value)}{Number(data.current.discount) > 0 ? ` − ${BRL(data.current.discount)}` : ""} = <b>{BRL(data.current.total)}</b>
                  {Number(data.current.discount) > 0 && <span className="block text-emerald-700 font-semibold" data-testid="item-history-current-discount">Desconto aplicado: {BRL(data.current.discount)}</span>}
                </p>
              </div>
            )}
            {(data.versions || []).length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">Nenhuma alteração registrada ainda.</p>
            ) : (
              (data.versions || []).map((v, idx) => (
                <div key={idx} className={`rounded-lg border p-3 ${v.visible_to_school === false ? "border-dashed border-slate-300 bg-slate-50 opacity-75" : "border-slate-200"}`} data-testid={`item-version-${idx}`}>
                  {v.idx !== undefined && (
                    <label className="flex items-center gap-2 mb-2 text-[11px] font-semibold text-slate-600 cursor-pointer" data-testid={`item-version-visible-${idx}`}>
                      <Checkbox checked={v.visible_to_school !== false} onCheckedChange={() => toggleVisible(v)} data-testid={`item-version-visible-check-${idx}`} />
                      Diretor vê esta alteração
                    </label>
                  )}
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      Versão anterior
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {fmt(v.snapshot_at)} · {v.user_name || "—"}
                    </p>
                  </div>
                  {v.reason && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1">
                      Motivo: {v.reason}
                    </p>
                  )}
                  <p className="text-sm text-slate-900">{v.description}</p>
                  {v.observation && <p className="text-xs text-slate-600 mt-0.5">{v.observation}</p>}
                  <p className="text-xs text-slate-700 mt-1">
                    Qtd {v.quantity} × {BRL(v.unit_value)}{Number(v.discount) > 0 ? ` − ${BRL(v.discount)}` : ""} = <b>{BRL(v.total)}</b>
                    {Number(v.discount) > 0 && <span className="block text-emerald-700">Desconto: {BRL(v.discount)}</span>}
                  </p>
                </div>
              ))
            )}
          </div>
        )}

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
